import React from 'react';
import { SidePanelHandle } from 'shared/components/side_panel';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Row, Person, RediscoveryApplication } from 'talent_matching/types';
import useCandidateSidePanel from 'talent_matching/hooks/useCandidateSidePanel';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { useRediscoveryRowActionsModal } from 'talent_matching/contexts/rediscovery_row_actions_modal_context';
// eslint-disable-next-line max-len
import RediscoveryApplicationHistory from 'talent_matching/components/side_panel/rediscovery/rediscovery_application_history';
import BaseSidePanel, { SidePanelNavigationProps } from './base_side_panel';
import RediscoverySidePanelHeader from './rediscovery_side_panel_header';

type Props = SidePanelNavigationProps & {
  candidatePrefetchBatch: Row[];
};

const TalentRediscoverySidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      selectCandidate,
      candidatePrefetchBatch,
    },
    ref
  ) => {
    const { hasOpenModal } = useRediscoveryRowActionsModal();
    const { activeSidePanelTab, setActiveSidePanelTab } =
      useRediscoveryTabContext();

    const { handlePanelRef, application, isLoading } = useCandidateSidePanel({
      applicationId,
      ref,
      prefetchCandidatesBatch: candidatePrefetchBatch,
      isRediscovery: true,
    });

    // Hook returns Person, but rediscovery data includes RediscoveryApplication fields at runtime
    const rediscoveryApp = application as Person & RediscoveryApplication;

    return (
      <BaseSidePanel
        handlePanelRef={handlePanelRef}
        application={rediscoveryApp}
        isLoading={isLoading}
        onClose={onClose}
        onToggle={onToggle}
        isVisible={isVisible}
        rowCount={rowCount}
        currentIndex={currentIndex}
        previousCandidate={previousCandidate}
        nextCandidate={nextCandidate}
        selectCandidate={selectCandidate}
        applicationId={applicationId}
        activeSidePanelTab={activeSidePanelTab}
        setActiveSidePanelTab={setActiveSidePanelTab}
        externalDisableClickOutside={hasOpenModal}
        externalModalsOpen={hasOpenModal}
        renderHeader={({ application: app, setLinkedinPreview }) => (
          <RediscoverySidePanelHeader
            application={app}
            setLinkedinPreview={setLinkedinPreview}
          />
        )}
      >
        {({
          application: app,
          isLoading: loading,
          setLinkedinPreview,
          extendedResumePreview,
          modalDocument,
          setExtendedResumePreview,
          setModalDocument,
          allowExtendedPreview,
        }) => (
          <Stack>
            {!loading && (
              <RediscoveryApplicationHistory
                historyUrl={app.applicationHistoryPath}
                activeDocument={extendedResumePreview ?? modalDocument ?? null}
                onOpenDocument={
                  allowExtendedPreview
                    ? (doc) => {
                        setModalDocument(null);
                        setLinkedinPreview(null);
                        setExtendedResumePreview(doc);
                      }
                    : (doc) => {
                        setExtendedResumePreview(null);
                        setModalDocument(doc);
                      }
                }
              />
            )}
          </Stack>
        )}
      </BaseSidePanel>
    );
  }
);

TalentRediscoverySidePanel.displayName = 'TalentRediscoverySidePanel';

export default TalentRediscoverySidePanel;
